import { ItemRow, PageSection, PriceRow } from '@src/components';
import { Apple, Simcard1 } from 'iconsax-react';
import { ReactComponent as PlayStore } from '@src/assets/icons/play-store.svg';
import { ResponsiveModal } from '@src/components/ResponsiveModal';
import { AndroidGuide } from '@src/components/InstallationGuide/components/AndroidGuide';
import { AppleGuide } from '@src/components/InstallationGuide/components';
import { useSelector } from 'react-redux';
import { selectLayout, THEME } from '@src/store/layout/layoutSlice';
import { ReactNode, useState } from 'react';
import { PaymentStatus } from '@src/types/payment';
import dayjs from 'dayjs';
import { ReactComponent as SuccessPaymentImage } from '@src/assets/images/SuccessPayment.svg';
import { ReactComponent as SuccessPaymentImageDark } from '@src/assets/images/SuccessPaymentDark.svg';
import { ReactComponent as FailedPaymentImage } from '@src/assets/images/FailedPayment.svg';
import { ReactComponent as FailedPaymentImageDark } from '@src/assets/images/FailedPaymentDark.svg';
import { OrderStatusTag } from './OrderStatusTag';
import { Button } from 'antd';

interface PaymentResultBaseProps {
  status?: PaymentStatus;
  idLabel: string;
  idValue: string;
  amount: number;
  currency: string;
  time: string | Date;
  provider?: ReactNode;
  extraContent?: ReactNode;
}

export function PaymentResultBase({
  status,
  idLabel,
  idValue,
  amount,
  currency,
  time,
  provider,
  extraContent,
}: PaymentResultBaseProps) {
  const layout = useSelector(selectLayout);

  const [showAppleGuide, setShowAppleGuide] = useState(false);
  const [showAndroidGuide, setShowAndroidGuide] = useState(false);

  return (
    <>
      <div className='flex items-center justify-center h-full p-4 bg-white dark:bg-black lg:bg-gradient-to-b lg:from-wl-surface lg:to-wl-primary-container overflow-auto overflow-x-hidden'>
        <div className='h-full w-full flex items-center justify-center'>
          <div className='lg:w-[480px] m-auto bg-wl-surface p-12 flex flex-col items-center'>
            {status === 'success' ? (
              layout.theme === THEME.LIGHT ? (
                <SuccessPaymentImage className='w-full max-h-[200]' />
              ) : (
                <SuccessPaymentImageDark className='w-full max-h-[200]' />
              )
            ) : status === 'failed' ? (
              layout.theme === THEME.LIGHT ? (
                <FailedPaymentImage className='w-full max-h-[200]' />
              ) : (
                <FailedPaymentImageDark className='w-full max-h-[200]' />
              )
            ) : null}

            <div className='w-full md:w-[327px]'>
              <h2 className='text-M-24 mb-4 text-wl-on-surface text-center'>
                {status === 'success'
                  ? 'Order Completed'
                  : 'Something went wrong'}
              </h2>
              {status === 'success' && (
                <h2 className='text-R-16 mb-4 text-wl-on-surface'>
                  <div>Thank you for your purchase</div>
                </h2>
              )}

              <PageSection className='bg-wl-background rounded-xl w-full mb-2 py-2 px-4 text-wl-on-background'>
                <ItemRow
                  label={idLabel}
                  value={idValue}
                  valueClassName='text-end w-full text-B-14'
                  labelClassName='text-nowrap'
                />
              </PageSection>

              <PageSection className='bg-wl-background rounded-xl mb-4 space-y-4 w-full py-2 px-4 text-wl-on-background'>
                <PriceRow label='Amount' currency={currency} price={amount} />
                <ItemRow
                  label='Time'
                  value={dayjs(time).format('MM/DD/YYYY - hh:mm A')}
                />
                {provider && <ItemRow label='Provider' value={provider} />}
                <ItemRow
                  label='Status'
                  labelClassName='text-R-16'
                  valueClassName='text-M-14 font-semibold'
                  value={<OrderStatusTag status={status} />}
                />
              </PageSection>

              {extraContent}
            </div>

            {status === 'success' && (
              <div className='mt-8 w-full'>
                <span className='text-M-16 mb-4 text-start text-wl-on-background'>
                  How to install:
                </span>
                <div className='mt-4 flex w-full justify-between gap-4'>
                  <Button
                    variant='solid'
                    className='shadow-none rounded-lg h-auto !py-2 !bg-wl-on-background w-full !text-wl-surface'
                    onClick={() => setShowAppleGuide(true)}
                  >
                    <Apple
                      variant='Bold'
                      size='32'
                      className='text-wl-surface'
                    />
                    IOS guide
                  </Button>
                  <Button
                    onClick={() => setShowAndroidGuide(true)}
                    className='shadow-none rounded-lg h-auto !py-2 !bg-wl-on-background w-full !text-wl-surface'
                  >
                    <PlayStore className='w-8 h-8' />
                    Android guide
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ResponsiveModal
        isOpen={showAppleGuide}
        title='How To Install eSIM?'
        headerIcon={<Simcard1 variant='Bulk' />}
        content={<AppleGuide onClose={() => setShowAppleGuide(false)} />}
        onClose={() => setShowAppleGuide(false)}
      />

      <ResponsiveModal
        isOpen={showAndroidGuide}
        title='How To Install eSIM?'
        headerIcon={<Simcard1 variant='Bulk' />}
        content={<AndroidGuide onClose={() => setShowAndroidGuide(false)} />}
        onClose={() => setShowAndroidGuide(false)}
      />
    </>
  );
}
